import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';

// Status stripe shown while admin mode is active. Now also serves as
// the mobile exit affordance — the AdminModeToggle pill in the desktop
// nav is hidden on small viewports (md:flex), so without a tappable
// Exit here, mobile admins had no way out except clearing the
// session. Banner is always visible on every authed page when
// adminMode is on, so this works regardless of which admin tab the
// user is on.
export const AdminModeBanner = () => {
  const { user } = useContext(AuthContext);
  const { adminMode, setAdminMode } = useAdminMode();
  const navigate = useNavigate();

  if (!user?.isAdmin || !adminMode) return null;

  const exit = () => {
    setAdminMode(false);
    if (user.userType === 'organizer') navigate('/organizer-bookings');
    else navigate('/dashboard');
  };

  return (
    <div className="bg-amber-500 text-amber-950 text-sm font-medium">
      <div className="max-w-7xl mx-auto px-4 py-2 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
        <span className="truncate">
          <span className="hidden sm:inline">Admin mode — platform chrome is hidden. You're acting as an admin, not as a {user.userType}.</span>
          <span className="sm:hidden">Admin mode active</span>
        </span>
        <button
          type="button"
          onClick={exit}
          aria-label="Exit admin mode"
          className="shrink-0 underline decoration-amber-950/40 hover:decoration-amber-950 font-semibold whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-950"
        >
          Exit
        </button>
      </div>
    </div>
  );
};

// Pill button for the nav. Only renders for admins. Clicking enters admin
// mode and routes to /admin; in admin mode it's the "exit" action.
export const AdminModeToggle = () => {
  const { user } = useContext(AuthContext);
  const { adminMode, setAdminMode } = useAdminMode();
  const navigate = useNavigate();

  if (!user?.isAdmin) return null;

  const handleClick = () => {
    if (adminMode) {
      setAdminMode(false);
      if (user.userType === 'organizer') navigate('/organizer-bookings');
      else navigate('/dashboard');
    } else {
      setAdminMode(true);
      navigate('/admin');
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`px-3 py-1.5 text-xs font-semibold rounded-lg border whitespace-nowrap transition ${
        adminMode
          ? 'bg-amber-500 text-amber-950 border-amber-500 hover:bg-amber-400'
          : 'bg-glass text-fg-muted border-line hover:border-line-strong hover:text-fg'
      }`}
      title={adminMode ? 'Exit admin mode' : 'Enter admin mode'}
    >
      {adminMode ? 'Exit admin' : 'Admin mode'}
    </button>
  );
};
